import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Flag, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { oapi } from '@/lib/officialsApi';
import { AddonSubscribeButton } from './AddonSubscribeButton';

const Pitch = ({ plan, ended }) => (
  <section className="lg:col-span-7 rounded-3xl bg-[#00247D] text-white p-8 sm:p-10 relative overflow-hidden" data-testid="upsell-pitch">
    <ShieldCheck className="absolute -right-10 -bottom-10 w-64 h-64 text-white/5" strokeWidth={1} />
    <p className="text-xs tracking-[0.2em] uppercase font-bold text-amber-300">Premium add-on</p>
    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[0.9] mt-3" style={{ fontFamily: 'Barlow Condensed' }}>
      Officials<br />Scheduling
    </h2>
    <p className="mt-5 text-white/80 max-w-lg text-sm sm:text-base">
      Assign referees, umpires and table officials to the games you already schedule here. Availability, conflict checks,
      email confirmations and reminders run on their own.
    </p>
    {ended && (
      <div className="mt-5 rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-sm max-w-lg" data-testid="addon-expired-note">
        Your add-on ended on {new Date(ended).toLocaleDateString()}. Renew to keep assigning officials. Your officials list and history are saved.
      </div>
    )}
    <div className="mt-8 flex items-end gap-2">
      <span className="text-6xl font-black leading-none" style={{ fontFamily: 'Barlow Condensed' }} data-testid="upsell-price">${plan?.price ?? 299}</span>
      <span className="text-white/70 mb-1"><span className="text-sm font-bold text-white/80" data-testid="upsell-currency">USD</span> / year</span>
    </div>
    <p className="text-sm text-white/70 mt-2">Works with any Schedule Guru plan, including Free. Officials always join free.</p>
    <AddonSubscribeButton className="mt-6 h-12 px-6 text-base" label={ended ? 'Renew now' : 'Subscribe'} />
    <p className="text-xs text-white/60 mt-3">Billed yearly by card (Visa, Mastercard) or PayPal. Turn off auto-renew any time.</p>
  </section>
);

export const OfficialsUpsell = ({ addon, isOfficial }) => {
  const [plan, setPlan] = useState(null);
  useEffect(() => { oapi('/officials/billing/plan').then(setPlan).catch(() => {}); }, []);
  const ended = addon && !addon.active && addon.status !== 'none' ? addon.expires_at : null;
  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14" data-testid="officials-upsell">
      <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
        <Pitch plan={plan} ended={ended} />
        <section className="lg:col-span-5 space-y-6">
          <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
            <h3 className="text-xl font-bold uppercase text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>What's included</h3>
            <ul className="mt-4 space-y-3" data-testid="upsell-features">
              {(plan?.features || []).map(f => (
                <li key={f} className="flex gap-3 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{f}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-6 flex items-start gap-4" data-testid="upsell-official-card">
            <span className="w-11 h-11 rounded-2xl bg-[#CF142B]/10 text-[#CF142B] flex items-center justify-center shrink-0"><Flag className="w-5 h-5" /></span>
            <div>
              <div className="font-bold text-slate-900">Are you a referee or official?</div>
              <p className="text-sm text-slate-600 mt-1">You never pay. Open your portal to set your availability and answer assignments.</p>
              <Link to="/officials/portal">
                <Button variant="outline" size="sm" className="mt-3" data-testid="upsell-open-portal-btn">{isOfficial ? 'Open my portal' : 'Go to the officials portal'}</Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};
